// @ts-strict-ignore
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';

import { getIsExpressionOperatorString } from 'flow/store/engine/utils';
import type {
  Action, DataRoute, Expression, ExpressionTerm,
} from 'flow/store/engine/types';
import { remapDataRoutePublicIdRefsIntoNames } from './mapping';

/**
 * recursively check if the incoming expression item is new by iterating through it
 * and call checkIfMatchedExpressionItemExist or itself recursively
 *
 * @param existingExpression the entire existing expression
 * @param incomingExpressionItem the new incoming expression item
 */
function recursivelyCheckIncomingExpression({
  existingExpression,
  incomingExpressionItem,
  actions,
  dataRoutes,
}: {
  existingExpression: Expression;
  incomingExpressionItem: ExpressionTerm[];
  actions: Record<string, Action>;
  dataRoutes: Record<string, DataRoute>;
}) {
  let unmatchedDataRoutes = [];

  // incomingExpressionItem was a nested array, we have to iterate through rule items
  // so the rule passed here is the array
  // in the array and get all threshold ids referenced that are new to the existing workflow data
  incomingExpressionItem.forEach((nestedItemInIncomingExpItem, index) => {
    if (Array.isArray(nestedItemInIncomingExpItem)) {
      // check if there's any unmatched non-identical expression item
      // and we do recursive call becaue it's an array
      const unmatchedExpressionItemsInNestedItem = recursivelyCheckIncomingExpression({
        existingExpression,
        incomingExpressionItem: nestedItemInIncomingExpItem,
        actions,
        dataRoutes,
      });

      // we want to get those that are not basically new = unmatched = not identical
      unmatchedDataRoutes = [
        ...unmatchedDataRoutes,
        ...unmatchedExpressionItemsInNestedItem,
      ];
    } else {
      // if the item is not array, we can go do comparison
      const expressionItemMatched = checkIfMatchedExpressionItemExist({
        existingExpression,
        incomingExpressionItem: nestedItemInIncomingExpItem,
        dataRoutes,
        actions,
      });

      if (!expressionItemMatched) {
        // if no matched identical expression item, this incoming expression item must be new...
        // ...if this is not an operator string!
        if (typeof nestedItemInIncomingExpItem === 'string'
          && getIsExpressionOperatorString(nestedItemInIncomingExpItem)) {
          return [];
        }

        // we want to bring the operator string used with it to retain the logic as much as we can
        if (index > 0) unmatchedDataRoutes.push(incomingExpressionItem[index - 1]);
        unmatchedDataRoutes.push(nestedItemInIncomingExpItem);
      }
    }

    return false;
  });

  return unmatchedDataRoutes;
}

/**
 * check if the expression item is new when compared with all of existing expression items
 * by remapping expression item for actual config data comparison
 *
 * @param existingExpression the entire existing expression
 * @param incomingExpressionItem the new incoming expression item
 */
function checkIfMatchedExpressionItemExist({
  existingExpression,
  incomingExpressionItem,
  actions,
  dataRoutes,
}: {
  existingExpression: Expression;
  incomingExpressionItem: ExpressionTerm | string;
  actions: Record<string, Action>;
  dataRoutes: Record<string, DataRoute>;
}) {
  // iterate through the expression item in the existingExpression and return anything that's identical
  // to the incoming new expression item
  const mapOfAllEqualityComparison = existingExpression
    .filter(existingExpressionItem => {
      if (Array.isArray(existingExpressionItem)) {
        // check if there's any matched identical expression item
        const expressionItemMatches = checkIfMatchedExpressionItemExist({
          existingExpression: existingExpressionItem,
          incomingExpressionItem,
          dataRoutes,
          actions,
        });

        return expressionItemMatches;
      }

      if (typeof existingExpressionItem === 'object' && 'data_route_id' in existingExpressionItem) {
        if (typeof incomingExpressionItem === 'object' && 'data_route_id' in incomingExpressionItem) {
          // if both existingExpressionItem and incomingExpressionItem are { data_route_id: string }
          // then we remap data route item data for comparison
          const incomingExpressionDataRoute = dataRoutes[incomingExpressionItem.data_route_id];
          const remappedExistingExpItem = remapDataRoutePublicIdRefsIntoNames({
            actions,
            dataRoute: cloneDeep(dataRoutes[existingExpressionItem.data_route_id]),
          });
          const remappedTargetDataRoute = remapDataRoutePublicIdRefsIntoNames({
            actions,
            dataRoute: cloneDeep(incomingExpressionDataRoute),
          });

          if (isEqual(remappedExistingExpItem, remappedTargetDataRoute)) {
            return true;
          }
        }
      }

      if (typeof existingExpressionItem !== 'object' && typeof incomingExpressionItem !== 'object') {
        if (typeof incomingExpressionItem !== 'object') {
          // if both existingExpressionItem and incomingExpressionItem are string or number
          if ((
            (typeof incomingExpressionItem === 'string' && !getIsExpressionOperatorString(incomingExpressionItem))
          || typeof incomingExpressionItem === 'number')
            && incomingExpressionItem === existingExpressionItem) {
          // we check if this is operator string or not... operator string is ignored at this point
            return true;
          }
        }
      }

      return false;
    });

  // if there's anything returned as identical in this, we return true
  // as in matched thresholds exist
  if (mapOfAllEqualityComparison.length) {
    return true;
  }

  return false;
}

/**
 * get any item in output attribute's expression that is new
 * when processing the pasted workflow data (this is used for partial paste user flow)
 *
 * @param incomingExpression the expression coming from the pasted data
 * @param existingExpression the expression in the existing action's output object
 */
export function getDeepMergeableExpressions({
  incomingExpression,
  existingExpression,
  actions,
  dataRoutes,
}: {
  incomingExpression: Expression;
  existingExpression: Expression;
  actions: Record<string, Action>;
  dataRoutes: Record<string, DataRoute>;
}) {
  let expressionsWithDifferences = [];
  // iterate thru incoming expression
  incomingExpression.forEach((item, index) => {
    // expression can have nested array.
    // in this case, this was not nested array, and it's either string operator (+-/*)
    // or user-input string, user-input number, or { data_route_id: string }
    if (!Array.isArray(item)) {
      // check if there's any matched identical expression item
      const matchedExpressionItem = checkIfMatchedExpressionItemExist({
        existingExpression,
        incomingExpressionItem: item,
        dataRoutes,
        actions,
      });

      if (!matchedExpressionItem) {
        // if no matched identical expression item, this incoming expression item must be new...
        // ...if this is not an operator string!
        if (typeof item === 'string' && getIsExpressionOperatorString(item)) {
          return false;
        }

        // we want to bring the operator string used with it to retain the logic as much as we can
        if (index > 0) expressionsWithDifferences.push(incomingExpression[index - 1]);
        expressionsWithDifferences.push(item);
      }
    }

    // in this case this is a nested array, so we need to iterate
    if (Array.isArray(item)) {
      const unmatchedExpressionItemsInNestedArrayItem = recursivelyCheckIncomingExpression({
        existingExpression,
        incomingExpressionItem: item,
        actions,
        dataRoutes,
      });

      // if any unmatched = new expression items identified from this nested array
      // we'll get all of them, so we add them into expressionsWithDifferences
      if (unmatchedExpressionItemsInNestedArrayItem.length) {
        expressionsWithDifferences = [
          ...expressionsWithDifferences,
          ...unmatchedExpressionItemsInNestedArrayItem,
        ];
      }
    }

    return false;
  });

  const mergeableExpressions = [];
  expressionsWithDifferences.forEach((expression, index) => {
    if (typeof expression === 'object' && 'publicId' in expression) {
      // this expression item is data route id object
      if (!mergeableExpressions
        .find(addedExpression => addedExpression?.publicId === expression.publicId)) {
        // check if there's any duplicates. if not, we add
        mergeableExpressions.push({
          data_route_id: expression.publicId,
        });
      }
    } else {
      mergeableExpressions.push(expression);
    }

    // by default we just extend them if there was no operator string found before this
    // we first check if there's any expression operator string already added to mergeableExpressions as the last item
    // then we check if the next item in expressionsWithDifferences is an operator string (if so, we should not add random new plus operator)
    if (!getIsExpressionOperatorString(mergeableExpressions[mergeableExpressions.length - 1])
      && (index + 1 < expressionsWithDifferences.length - 1
      && !getIsExpressionOperatorString(expressionsWithDifferences[index + 1]))
      && index < expressionsWithDifferences.length - 1) {
      mergeableExpressions.push('+');
    }
  });

  return mergeableExpressions;
}
